import React, { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, Briefcase, CalendarClock, CheckCircle2, Loader2, User, X } from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import Modal from '../shared/Modal';
import SearchableSelect from '../shared/SearchableSelect';

/**
 * Send an interview invitation on behalf of an employer (Employer
 * Phase 3 §7). The invitation runs through the employer's OWN
 * machinery — their availability drives the time slots the applicant
 * can pick, and their meeting setup applies — so the applicant's
 * experience is identical to an employer-sent invite. The record is
 * stamped as sent on behalf and the action is audited.
 */

interface AdminInterviewOnBehalfModalProps {
  employers: Array<{ id: string; companyName: string }>;
  onClose: () => void;
  onDone: () => void;
}

export default function AdminInterviewOnBehalfModal({ employers, onClose, onDone }: AdminInterviewOnBehalfModalProps) {
  const [step, setStep] = useState(1);
  const [employerId, setEmployerId] = useState('');
  const [candidates, setCandidates] = useState<any[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidateId, setCandidateId] = useState('');
  const [schedulingMethod, setSchedulingMethod] = useState<'applicant_chooses' | 'employer_picks'>('applicant_chooses');
  const [interviewType, setInterviewType] = useState<'virtual' | 'in-person'>('virtual');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [meetingLocation, setMeetingLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);

  const employer = employers.find((e) => e.id === employerId) || null;
  const candidate = candidates.find((c) => c.id === candidateId) || null;

  useEffect(() => {
    if (!employerId) { setCandidates([]); setCandidateId(''); return; }
    setCandidatesLoading(true);
    setCandidateId('');
    safeFetch(`/api/admin/offers/candidates?employerId=${encodeURIComponent(employerId)}`)
      .then((d: any) => setCandidates((d.candidates || []).filter((c: any) => c.jobId && c.applicantId)))
      .catch(() => setCandidates([]))
      .finally(() => setCandidatesLoading(false));
  }, [employerId]);

  const send = async () => {
    if (!employer || !candidate) return;
    if (schedulingMethod === 'employer_picks' && (!scheduledDate || !scheduledTime)) {
      notifyToast('error', 'Please pick a date and time, or let the applicant choose.');
      return;
    }
    setSending(true);
    try {
      await safeFetch('/api/admin/interviews/on-behalf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employerId: employer.id,
          applicationId: candidate.id,
          applicantId: candidate.applicantId,
          jobId: candidate.jobId,
          schedulingMethod,
          interviewType,
          allowApplicantChooseDateTime: schedulingMethod === 'applicant_chooses',
          scheduledDate: schedulingMethod === 'employer_picks' ? scheduledDate : undefined,
          scheduledTime: schedulingMethod === 'employer_picks' ? scheduledTime : undefined,
          timeZone: 'America/New_York',
          durationMinutes,
          meetingLocation: interviewType === 'in-person' ? (meetingLocation.trim() || undefined) : undefined,
          notes: notes.trim() || undefined,
        }),
      });
      onDone();
    } catch (err: any) {
      notifyToast('error', err?.message || 'The invitation could not be sent.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-black text-slate-900 flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-blue-600" />
              Send an interview invitation on behalf of an employer
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {step === 1 ? 'Choose the employer and candidate.' : 'Set up the interview, then check and send.'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 cursor-pointer" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label htmlFor="ibh-employer" className="block text-sm font-bold text-slate-700 mb-1.5">Employer</label>
              <SearchableSelect
                id="ibh-employer"
                value={employerId}
                onChange={(v) => setEmployerId(v)}
                options={employers.map((e) => ({ value: e.id, label: e.companyName }))}
                placeholder="Search employers…"
                clearLabel="Choose the employer…"
                emptyLabel="No employers found"
              />
            </div>
            {employerId && (
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">Candidate shared with this employer</label>
                {candidatesLoading ? (
                  <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading shared candidates…</p>
                ) : candidates.length === 0 ? (
                  <p className="text-sm text-slate-500">No candidates with an application are shared with this employer yet.</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {candidates.map((c) => {
                      const selected = candidateId === c.id;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setCandidateId(c.id)}
                          className={`w-full text-left rounded-xl border p-3 transition cursor-pointer ${selected ? 'border-blue-500 bg-blue-50/60' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                        >
                          <span className="flex items-center gap-2 text-sm font-bold text-slate-800">
                            <User className="h-4 w-4 text-slate-400" /> {c.applicantName || 'Candidate'}
                          </span>
                          <span className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                            <Briefcase className="h-3.5 w-3.5 text-slate-400" /> {c.jobTitle || 'Job'}{c.programType ? ` · ${c.programType}` : ''}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer">Cancel</button>
              <button
                onClick={() => setStep(2)}
                disabled={!employer || !candidate}
                className="px-5 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition cursor-pointer flex items-center gap-2 disabled:opacity-50"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && employer && candidate && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2">
              {[
                ['Candidate', candidate.applicantName || '—'],
                ['Job', candidate.jobTitle || '—'],
                ['Interview with (employer)', employer.companyName],
                ['Sent by', 'You — ISATS admin, on behalf of the employer'],
              ].map(([label, value]) => (
                <div key={label} className="flex items-baseline justify-between gap-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400 shrink-0">{label}</span>
                  <span className="text-sm font-bold text-slate-800 text-right break-words">{value}</span>
                </div>
              ))}
            </div>

            <div>
              <span className="block text-sm font-bold text-slate-700 mb-1.5">How should the time be set?</span>
              <div className="grid sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setSchedulingMethod('applicant_chooses')}
                  className={`text-left rounded-xl border p-3 cursor-pointer transition ${schedulingMethod === 'applicant_chooses' ? 'border-blue-500 bg-blue-50/60' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                >
                  <span className="block text-sm font-bold text-slate-800">Let the applicant choose a time</span>
                  <span className="block text-xs text-slate-500 mt-1">Times come from {employer.companyName}'s own availability.</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSchedulingMethod('employer_picks')}
                  className={`text-left rounded-xl border p-3 cursor-pointer transition ${schedulingMethod === 'employer_picks' ? 'border-blue-500 bg-blue-50/60' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                >
                  <span className="block text-sm font-bold text-slate-800">Pick a date and time</span>
                  <span className="block text-xs text-slate-500 mt-1">The invitation proposes this exact time.</span>
                </button>
              </div>
            </div>

            {schedulingMethod === 'employer_picks' && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="ibh-date" className="block text-sm font-bold text-slate-700 mb-1.5">Date</label>
                  <input id="ibh-date" type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-brand-primary/30" />
                </div>
                <div>
                  <label htmlFor="ibh-time" className="block text-sm font-bold text-slate-700 mb-1.5">Time</label>
                  <input id="ibh-time" type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-brand-primary/30" />
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="ibh-type" className="block text-sm font-bold text-slate-700 mb-1.5">Interview type</label>
                <select id="ibh-type" value={interviewType} onChange={(e) => setInterviewType(e.target.value as 'virtual' | 'in-person')} className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-brand-primary/30 bg-white">
                  <option value="virtual">Online (video call)</option>
                  <option value="in-person">In person</option>
                </select>
                {interviewType === 'virtual' && (
                  <p className="text-xs text-slate-500 mt-1.5">Uses the employer's meeting setup — a portal video room is created if they have not added their own link.</p>
                )}
              </div>
              <div>
                <label htmlFor="ibh-duration" className="block text-sm font-bold text-slate-700 mb-1.5">Length</label>
                <select id="ibh-duration" value={durationMinutes} onChange={(e) => setDurationMinutes(Number(e.target.value))} className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-brand-primary/30 bg-white">
                  {[15, 30, 45, 60, 90].map((m) => (<option key={m} value={m}>{m} minutes</option>))}
                </select>
              </div>
            </div>

            {interviewType === 'in-person' && (
              <div>
                <label htmlFor="ibh-location" className="block text-sm font-bold text-slate-700 mb-1.5">Where will it happen? <span className="font-semibold text-slate-400">(optional)</span></label>
                <input id="ibh-location" type="text" value={meetingLocation} onChange={(e) => setMeetingLocation(e.target.value)} placeholder="Office address or meeting place" className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-brand-primary/30" />
              </div>
            )}

            <div>
              <label htmlFor="ibh-notes" className="block text-sm font-bold text-slate-700 mb-1.5">Note for the candidate <span className="font-semibold text-slate-400">(optional)</span></label>
              <textarea id="ibh-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Anything the candidate should know before the interview." className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-brand-primary/30 resize-none" />
            </div>

            <div className="flex items-center justify-between pt-2">
              <button onClick={() => setStep(1)} disabled={sending} className="px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition cursor-pointer flex items-center gap-2 disabled:opacity-50">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button
                onClick={send}
                disabled={sending}
                className="px-5 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition cursor-pointer flex items-center gap-2 disabled:opacity-50"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {sending ? 'Sending…' : 'Send the invitation'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
